import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useExam } from "../context/ExamContext";
import { getStudentResults } from "../api/client";

import AppSidebar from "../pages/AppSidebar";

/* =========================================================
   HELPERS
   ========================================================= */

function formatDate(dateStr) {
  if (!dateStr) return "—";

  const d = new Date(dateStr);

  if (Number.isNaN(d.getTime())) {
    return dateStr;
  }

  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function isPassResult(result) {
  return (
    typeof result === "string" &&
    result.toUpperCase().startsWith("PASS")
  );
}

/*
 * Backend is the source of truth for publication.
 *
 * result_available:
 *   true  -> score/result can be shown
 *   false -> score/result must remain hidden
 *
 * We also support is_published as a fallback so this page
 * remains compatible if the backend returns that field.
 */
function getRowStatus(result) {
  const resultAvailable =
    result?.result_available === true ||
    result?.is_published === true;

  if (!resultAvailable) {
    return {
      state: "pending",
      percent: null,
      showMark: false,
    };
  }

  const score = Number(result?.score ?? 0);

  /*
   * Prefer the percentage calculated by the backend because
   * marks/question are configurable.
   *
   * Fallback calculation is only used if the backend does
   * not provide percentage.
   */
  let percent = null;

  if (
    result?.percentage !== null &&
    result?.percentage !== undefined &&
    result?.percentage !== ""
  ) {
    percent = Number(result.percentage);
  } else {
    const total = Number(
      result?.maximum_marks ??
        result?.max_marks ??
        result?.total_marks ??
        result?.total ??
        0
    );

    if (total > 0) {
      percent = Math.round((score / total) * 100);
    }
  }

  if (percent !== null) {
    percent = Math.max(0, Math.min(100, percent));
  }

  return {
    state: isPassResult(result?.result)
      ? "passed"
      : "failed",
    percent,
    showMark: true,
  };
}

/*
 * Normalize backend field names.
 *
 * The current backend result API uses snake_case while
 * older frontend code used camelCase. Supporting both here
 * prevents unnecessary frontend breakage.
 */
function normalizeResult(result) {
  return {
    ...result,

    id: result?.id,

    attemptId:
      result?.attempt_id ??
      result?.attemptId ??
      null,

    attemptNumber:
      result?.attempt_number ??
      result?.attemptNumber ??
      1,

    examId:
      result?.exam_id ??
      result?.examId ??
      null,

    examName:
      result?.exam_name ??
      result?.examName ??
      "—",

    courseName:
      result?.course_name ??
      result?.courseName ??
      result?.exam_name ??
      result?.examName ??
      "—",

    examDate:
      result?.exam_date ??
      result?.examDate ??
      null,

    submittedAt:
      result?.submitted_at ??
      result?.submittedAt ??
      null,

    availableAt:
      result?.available_at ??
      result?.availableAt ??
      null,

    publishedAt:
      result?.published_at ??
      result?.publishedAt ??
      null,

    score:
      result?.score !== null &&
      result?.score !== undefined
        ? Number(result.score)
        : null,

    total:
      result?.total !== null &&
      result?.total !== undefined
        ? Number(result.total)
        : null,

    maximumMarks:
      result?.maximum_marks ??
      result?.max_marks ??
      result?.total_marks ??
      null,

    percentage:
      result?.percentage !== null &&
      result?.percentage !== undefined
        ? Number(result.percentage)
        : null,

    resultStatus:
      result?.result ??
      result?.status ??
      null,

    resultAvailable:
      result?.result_available === true ||
      result?.is_published === true,
  };
}

/* =========================================================
   SCORE BOARD
   ========================================================= */

export default function ScoreBoard() {
  const { currentUser, logout } = useExam();

  const navigate = useNavigate();

  const [scores, setScores] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  /* =======================================================
     LOAD RESULTS FROM DJANGO
     ======================================================= */

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    let mounted = true;

    setLoading(true);
    setError("");

    getStudentResults()
      .then(({ data }) => {
        if (!mounted) return;

        const resultList = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
          ? data.results
          : [];

        setScores(resultList);
      })
      .catch((err) => {
        console.error(
          "Failed to load student results:",
          err
        );

        if (!mounted) return;

        setScores([]);

        setError(
          "Could not load your examination results."
        );
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [currentUser]);

  /* =======================================================
     PREPARE SCORE BOARD ROWS
     ======================================================= */

  const rows = useMemo(() => {
    return scores.map((score) => {
      const normalized = normalizeResult(score);

      return {
        ...normalized,
        ...getRowStatus(normalized),
      };
    });
  }, [scores]);

  /* =======================================================
     STATISTICS
     ======================================================= */

  const stats = useMemo(() => {
    const total = rows.length;

    const passed = rows.filter(
      (row) => row.state === "passed"
    ).length;

    const failed = rows.filter(
      (row) => row.state === "failed"
    ).length;

    const pending = rows.filter(
      (row) => row.state === "pending"
    ).length;

    /*
     * Only published results are included in average.
     *
     * Pending scores must never affect the average.
     */
    const visibleRows = rows.filter(
      (row) =>
        row.state !== "pending" &&
        row.percent !== null
    );

    const avgPercent =
      visibleRows.length > 0
        ? Math.round(
            visibleRows.reduce(
              (sum, row) =>
                sum + Number(row.percent || 0),
              0
            ) / visibleRows.length
          )
        : null;

    return {
      total,
      passed,
      failed,
      pending,
      avgPercent,
    };
  }, [rows]);

  /* =======================================================
     LOGOUT
     ======================================================= */

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <div className="oes-page-full">
      <div className="oes-home-shell d-flex flex-row overflow-hidden">

        <AppSidebar />

        <div className="flex-grow-1 p-3 p-md-4">

          {/* =================================================
              HEADER
              ================================================= */}

          <div className="d-flex justify-content-between align-items-start mb-3">

            <button
              className="btn btn-link btn-sm text-secondary text-decoration-none ps-0"
              onClick={() => navigate("/home")}
            >
              <i className="bi bi-arrow-left me-1" />
              Back to Home
            </button>

            <div className="d-flex align-items-center gap-3">

              <i className="bi bi-bell fs-5 text-secondary" />

              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleLogout}
              >
                <i className="bi bi-box-arrow-right me-1" />
                Logout
              </button>

            </div>
          </div>

          {/* =================================================
              TITLE
              ================================================= */}

          <h2 className="h5 mb-3">
            Score Board
          </h2>

          {/* =================================================
              ERROR
              ================================================= */}

          {error && (
            <div
              className="alert alert-danger small"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* =================================================
              STATISTICS
              ================================================= */}

          {!loading && rows.length > 0 && (
            <div className="row g-3 mb-4">

              {/* Exams Taken */}

              <div className="col-6 col-md-3">
                <div className="stat-card">

                  <span className="stat-icon">
                    <i className="bi bi-journal-check" />
                  </span>

                  <div className="stat-value">
                    {stats.total}
                  </div>

                  <div className="stat-label">
                    Exams Taken
                  </div>

                </div>
              </div>

              {/* Passed */}

              <div className="col-6 col-md-3">
                <div className="stat-card">

                  <span className="stat-icon tone-green">
                    <i className="bi bi-check-circle" />
                  </span>

                  <div className="stat-value">
                    {stats.passed}
                  </div>

                  <div className="stat-label">
                    Passed
                  </div>

                </div>
              </div>

              {/* Failed */}

              <div className="col-6 col-md-3">
                <div className="stat-card">

                  <span className="stat-icon tone-red">
                    <i className="bi bi-x-circle" />
                  </span>

                  <div className="stat-value">
                    {stats.failed}
                  </div>

                  <div className="stat-label">
                    Failed
                  </div>

                </div>
              </div>

              {/* Pending */}

              <div className="col-6 col-md-3">
                <div className="stat-card">

                  <span className="stat-icon">
                    <i className="bi bi-hourglass-split" />
                  </span>

                  <div className="stat-value">
                    {stats.pending}
                  </div>

                  <div className="stat-label">
                    Result Pending
                  </div>

                </div>
              </div>

            </div>
          )}

          {/* =================================================
              SCORE BOARD CARD
              ================================================= */}

          <div className="oes-card p-3 p-md-4">

            {/* Loading */}

            {loading && (
              <p className="text-muted mb-0">
                Loading...
              </p>
            )}

            {/* No results */}

            {!loading && rows.length === 0 && (
              <p className="text-muted small mb-0">
                No exams completed yet. Take an exam
                to see your score here.
              </p>
            )}

            {/* Results */}

            {!loading && rows.length > 0 && (
              <div className="table-responsive">

                <table className="table align-middle scoreboard-table mb-0">

                  <thead>
                    <tr>

                      <th>
                        Course
                      </th>

                      <th>
                        Exam
                      </th>

                      <th>
                        Attempt
                      </th>

                      <th>
                        Exam Date
                      </th>

                      <th>
                        Score
                      </th>

                      <th>
                        Result
                      </th>

                    </tr>
                  </thead>

                  <tbody>

                    {rows.map((row, index) => {

                      const key =
                        row.id ??
                        row.attemptId ??
                        `${row.examId}-${row.attemptNumber}-${index}`;

                      const showMark =
                        row.state === "passed" ||
                        row.state === "failed";

                      const displayTotal =
                        row.maximumMarks ??
                        row.total;

                      return (
                        <tr key={key}>

                          {/* COURSE */}

                          <td className="fw-semibold">
                            {row.courseName}
                          </td>

                          {/* EXAM */}

                          <td className="small">
                            {row.examName}
                          </td>

                          {/* ATTEMPT */}

                          <td className="text-nowrap">

                            <span className="badge bg-light text-dark border">

                              Attempt{" "}
                              {row.attemptNumber}

                            </span>

                          </td>

                          {/* DATE */}

                          <td className="text-muted small text-nowrap">

                            {formatDate(
                              row.examDate
                            )}

                          </td>

                          {/* SCORE */}

                          <td>

                            {showMark ? (

                              <div className="d-flex align-items-center gap-2">

                                {/* Score bar */}

                                {row.percent !== null && (
                                  <div
                                    className="score-bar-track"
                                    style={{
                                      minWidth: "90px",
                                    }}
                                  >
                                    <div
                                      className="score-bar-fill"
                                      style={{
                                        width: `${Math.min(
                                          100,
                                          Math.max(
                                            0,
                                            row.percent
                                          )
                                        )}%`,
                                        background:
                                          row.state ===
                                          "passed"
                                            ? "#1c8a5a"
                                            : "#c0392b",
                                      }}
                                    />
                                  </div>
                                )}

                                {/* Score */}

                                <span className="small fw-semibold text-nowrap">

                                  {row.score !== null &&
                                  row.score !== undefined
                                    ? displayTotal !==
                                        null &&
                                      displayTotal !==
                                        undefined
                                      ? `${row.score}/${displayTotal}`
                                      : row.score
                                    : "—"}

                                </span>

                                {/* Percentage */}

                                {row.percent !==
                                  null && (
                                  <span className="text-muted small text-nowrap">

                                    {row.percent}%

                                  </span>
                                )}

                              </div>

                            ) : (

                              <span className="text-muted small fst-italic">

                                Result Pending

                              </span>

                            )}

                          </td>

                          {/* RESULT */}

                          <td>

                            {row.state ===
                              "pending" && (
                              <span
                                className="result-pill"
                                style={{
                                  background:
                                    "#e9ecef",
                                  color:
                                    "#495057",
                                }}
                              >
                                Result Pending
                              </span>
                            )}

                            {showMark && (
                              <span
                                className={`result-pill ${
                                  row.state ===
                                  "passed"
                                    ? "pass"
                                    : "fail"
                                }`}
                              >
                                {row.resultStatus ||
                                  (row.state ===
                                  "passed"
                                    ? "PASS"
                                    : "FAIL")}
                              </span>
                            )}

                          </td>

                        </tr>
                      );
                    })}

                  </tbody>

                </table>

              </div>
            )}

          </div>

        </div>

      </div>
    </div>
  );
}